import type React from 'react';
import { z } from 'zod';
import { str } from '../../../platform/api/utils';
export const STATUS_VARIANTS: Record<string, string> = {
  ACTIVE: 'success',
  AWAY: 'warning',
  SUSPENDED: 'danger',
  DRAFT: 'info',
  DISCONNECTED: 'default',
  DECEASED: 'default',
  ARCHIVED: 'default',
};

export const createSchema = z.object({
  civilName: z.string().trim().min(1, 'Nome de exibição / nome civil é obrigatório'),
  socialName: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  membershipType: z.string().optional(),
  status: z.string().optional(),
});

export type CreateForm = z.infer<typeof createSchema>;

export function displayName(p: Record<string, unknown>): string {
  return str(p.socialName) || str(p.civilName) || str(p.name) || '—';
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: '0.8rem',
        color: 'var(--ink)',
        fontWeight: 600,
      }}
    >
      <span>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>{error}</span>}
    </label>
  );
}

export function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: '0.625rem 0.75rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

export function Td(props: React.HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td style={{ padding: '0.625rem 0.75rem', fontSize: '0.875rem', ...props.style }}>
      {props.children}
    </td>
  );
}

export function PagBtn({
  active,
  disabled,
  onClick,
  label,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 32,
        height: 32,
        padding: '0 0.5rem',
        border: active ? '1px solid var(--green)' : '1px solid var(--line)',
        borderRadius: 10,
        background: active ? 'var(--green)' : '#fff',
        color: active ? '#fff' : 'var(--muted)',
        fontSize: '0.8rem',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );
}

export function Skeleton() {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid var(--line)',
        padding: '1.25rem 1.5rem',
      }}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: '2rem',
            padding: '0.5rem 0',
            borderBottom: i < 4 ? '1px solid var(--line-soft)' : 'none',
          }}
        >
          <div
            style={{ width: 160, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
          />
          <div style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }} />
          <div
            style={{ width: 120, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
          />
        </div>
      ))}
    </div>
  );
}

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};
export const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: '1.5rem',
  width: '100%',
  maxWidth: 440,
  boxShadow: '0 8px 32px rgba(38, 49, 39, 0.08)',
};
export const inputCss: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.875rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: 600,
};
export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
